import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Hide Emergent badge
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => { if (badge) badge.style.display = 'flex'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setEmailSent(true);
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Something went wrong',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send reset email. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="pt-[env(safe-area-inset-top,12px)]" />
        
        <header className="px-4 py-4 flex items-center gap-3 border-b border-gray-200">
          <button onClick={() => navigate('/login')} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Check Your Email</h1>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Email Sent!</h2>
          <p className="text-gray-600 text-center mb-8 max-w-sm">
            If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
          </p>
          
          <p className="text-gray-500 text-sm text-center mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>

          <div className="space-y-3 w-full max-w-sm">
            <Button
              onClick={() => setEmailSent(false)}
              variant="outline"
              className="w-full py-6 rounded-full"
            >
              Try Another Email
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-700 hover:bg-blue-800 py-6 rounded-full"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      <header className="px-4 py-4 flex items-center gap-3 border-b border-gray-200">
        <button onClick={() => navigate('/login')} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Forgot Password</h1>
          <p className="text-sm text-gray-600">Reset your password</p>
        </div>
      </header>

      <div className="flex-1 px-6 py-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 mx-auto">
          <Mail className="w-8 h-8 text-blue-700" />
        </div>

        <p className="text-gray-600 text-center mb-8">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 max-w-sm mx-auto">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-900 font-medium">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full"
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <div className="text-center mt-6">
          <span className="text-gray-600">Remember your password? </span>
          <button onClick={() => navigate('/login')} className="text-blue-700 font-semibold hover:underline">
            Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
